import { ChangesDetection } from "../../../../change-detection";
import { filter, map, Observable, of, shareReplay, startWith, switchMap } from "rxjs";
import { I_ChartConfigResponse, I_ChartBaseConfigResponse, I_ChartAxisConfigResponse, I_ChartSerieConfigResponse, I_ChartCandleConfigResponse, I_VisualMapResponse, I_VisualMapPieceResponse, I_ChartAxisLineResponse, I_AxisLineStyleResponse, I_ChartAxisTickResponse, I_ChartAxisLabelResponse } from "../api/chart-config.response";
import { C_ConfigObservation } from "../../observation/domain/config-observation.model";
import { E_ChangeAction } from "../../../../../enums/change-action.enum";
import { E_ChartAxisTypeResponse, E_ChartAxisPositionResponse, E_ChartMarkerIcon, E_ChartAxisPosition, E_ChartAxisType } from "../../../../../enums/smart-display/graphs/axis-configs.enum";
import { E_CandleValueType } from "../../../../../enums/smart-display/graphs/chart-candle-value-types.enum";
import { E_LineType } from "../../../../../enums/smart-display/graphs/chart-line-type.enum";
import { E_SmartDisplayGraphResult } from "../../../../../enums/smart-display/graphs/graph-result.enum";
import { E_SmartDisplayGraphType } from "../../../../../enums/smart-display/graphs/graph-type.enum";
import { E_SmartDisplayGraphLabel } from "../../../../../enums/smart-display/graphs/label.enum";
import { E_GraphSerieSourceType } from "../../../../../enums/smart-display/graphs/source-type.enum";


export class C_ChartConfigList extends ChangesDetection<T_ChartConfigChange> {
  private list: Map<string, C_ChartConfig> = new Map();
  private cachedSubs = new Map<string, Observable<C_ChartConfig | undefined>>();

  constructor() {
    super();
  }

  getChartGroup(group: string): C_ChartConfig | undefined {
    return this.list.get(group);
  }

  getLiveChartGroup(group: string, obsOnGraph?: C_ConfigObservation | undefined): Observable<C_ChartConfig | undefined> {
    if (this.cachedSubs.has(group)) return this.cachedSubs.get(group)!;

    const stream$ = this.getForcedChanges().pipe(
      startWith(null),
      // Filtrado estricto: Solo pasa si el cambio afecta a este grupo específico o es masivo
      filter((change) => {
        if (!change) return true; // Permite el startWith(null) inicial
        if (change.action === E_ChangeAction.Reset) return true;
        if (change.action === E_ChangeAction.AddBulk) return change.groups.has(group);
        return change.group === group;
      }),
      map(() => this.list.get(group) ?? new C_ChartConfig().getDefault(group, obsOnGraph)),
      // Si la instancia interna del gráfico cambia, se suscribe a sus cambios internos
      switchMap(chartConfig => {
        if (!chartConfig) return of(undefined);
        return chartConfig.getForcedChanges().pipe(
          startWith(chartConfig),
          map(() => chartConfig)
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.cachedSubs.set(group, stream$);
    return stream$;
  }

  addChartConfigArrays(charts: I_ChartConfigResponse[]) {
    const bulkGroups = new Set<string>();
    charts.forEach((chartConf) => {
      const group = chartConf.baseConfig.name;
      if (!group) return;
      bulkGroups.add(group);

      if (this.list.has(group)) {
        this.list.get(group)!.initializeFromJson(chartConf);
      } else {
        this.list.set(group, new C_ChartConfig(chartConf));
      }
    });
    this.forceUpdateDetection({ action: E_ChangeAction.AddBulk, groups: bulkGroups, data: charts });
  }

  addNewChartConfig(info: I_ChartConfigResponse) {
    const group = info.baseConfig.group;
    const action = this.list.has(group) ? E_ChangeAction.Update : E_ChangeAction.Add;

    if (action === E_ChangeAction.Update) {
      this.list.get(group)!.initializeFromJson(info);
    } else {
      this.list.set(group, new C_ChartConfig(info));
    }

    this.forceUpdateDetection({ action, group, data: info });
  }

  clearSubs() {
    this.cachedSubs.clear();
    this.forceUpdateDetection({ action: E_ChangeAction.Reset });
  }
}


export type T_ChartConfigChange =
  | { action: E_ChangeAction.Add; group: string; data: I_ChartConfigResponse }
  | { action: E_ChangeAction.Update; group: string; data: I_ChartConfigResponse }
  | { action: E_ChangeAction.AddBulk; groups: Set<string>; data: I_ChartConfigResponse[] }
  | { action: E_ChangeAction.Reset };

export class C_ChartConfig extends ChangesDetection {
  baseConfig: C_ChartBaseConfig = new C_ChartBaseConfig();
  axesConfig: ChartAxesConfigList = new ChartAxesConfigList();
  seriesConfig: C_ChartSerieConfig[] = [];
  constructor(chartConfig?: I_ChartConfigResponse) {
    super();
    if (!chartConfig) return;
    this.initializeFromJson(chartConfig);
  }

  initializeFromJson(chartConfig: I_ChartConfigResponse) {
    if (chartConfig.hasOwnProperty('axesConfig')) chartConfig.axesConfig.forEach(axis => this.axesConfig.addAxis(axis));
    if (chartConfig.hasOwnProperty('baseConfig')) this.baseConfig = new C_ChartBaseConfig(chartConfig.baseConfig);
    if (chartConfig.hasOwnProperty('seriesConfig')) this.seriesConfig = chartConfig.seriesConfig.map(sc => new C_ChartSerieConfig(sc));
    this.forceUpdateDetection();
  }

  getSeriesByType(type: E_SmartDisplayGraphType): C_ChartSerieConfig[] {
    return this.seriesConfig.filter(serie => serie.type == type);
  }

  getDefault(name: string, obsOnGraph?: C_ConfigObservation | undefined): C_ChartConfig {
    let chartConf = new C_ChartConfig();
    chartConf.baseConfig.name = name;
    chartConf.baseConfig.title = name;
    chartConf.baseConfig.group = name;
    chartConf.baseConfig.numValues = 24;
    chartConf.baseConfig.showGrid = true;

    let yAxis = {
      axisLine: {
        show: true,
        lineStyle: {
          color: "#fff"
        }
      },
      // min: 'dataMin',
      // max: 'dataMax',
      axisTick: {
        "show": true,
        "length": 7
      },
      axisLabel: {
        "show": true,
        "color": "#fff",
        "fontSize": 10,
        "margin": 0,
      },
      offset: 0,
      labelFormat: E_SmartDisplayGraphLabel.Default,
      customLabels: [],
      keyName: 'mainYAxis',
      type: E_ChartAxisTypeResponse.Value,
      show: true,
      position: E_ChartAxisPositionResponse.Left,
      regularity: E_SmartDisplayGraphResult.Last,
    } as I_ChartAxisConfigResponse;
    let xAxis = {
      axisLine: {
        show: true,
        lineStyle: {
          color: "#fff"
        }
      },
      min: undefined,
      max: undefined,
      axisTick: {
        "show": true,
        "length": 7
      },
      axisLabel: {
        "show": true,
        "color": "#fff",
        "fontSize": 10,
        "margin": 0
      },
      offset: 0,
      labelFormat: E_SmartDisplayGraphLabel.Hour,
      customLabels: [],
      position: E_ChartAxisPositionResponse.Bottom,
      keyName: "mainXAxis",
      type: E_ChartAxisTypeResponse.Value,
      show: true,
      regularity: E_SmartDisplayGraphResult.Hour,
    } as I_ChartAxisConfigResponse;
    chartConf.axesConfig.addAxis(yAxis)
    chartConf.axesConfig.addAxis(xAxis)
    let serie: any = new class implements I_ChartSerieConfigResponse {
      key = name;
      linewidth = 2;
      color = "#FFF";
      type = E_SmartDisplayGraphType.Line;
      sourceType = E_GraphSerieSourceType.Obs;
      showSymbol = false;
      showColorOnLegend = false;
      values = E_SmartDisplayGraphResult.Last;
      axesNames = ["mainXAxis", "mainYAxis"];
      aboveBaselineColor = "#ED4965";
      belowBaselineColor: string = "#ED4965";
      markerIcon: E_ChartMarkerIcon = E_ChartMarkerIcon.None;
      showOnLegend: boolean = false;
      candleKeyList?: I_ChartCandleConfigResponse[] | undefined;
      balanceIntervalMs: number = 0;
    }
    serie.visualMap = this.buildVisualMap(name, obsOnGraph);
    chartConf.seriesConfig.push(
      new C_ChartSerieConfig(serie)
    )
    return chartConf;
  }

  private buildVisualMap(name: string, obsOnGraph?: C_ConfigObservation): any | undefined {
    if (!obsOnGraph) return undefined;

    const maxAlert = typeof obsOnGraph.maxAlert === 'number' ? obsOnGraph.maxAlert : undefined;
    const maxWarn = typeof obsOnGraph.maxWarn === 'number' ? obsOnGraph.maxWarn : undefined;
    const minWarn = typeof obsOnGraph.minWarn === 'number' ? obsOnGraph.minWarn : undefined;
    const minAlert = typeof obsOnGraph.minAlert === 'number' ? obsOnGraph.minAlert : undefined;

    const pieces: any[] = [];
    const step = 1; // Margen de transición para evitar colisión de coordenadas en ECharts

    // 1. Tramo Crítico Superior (> maxAlert)
    if (maxAlert !== undefined) {
      pieces.push({
        gt: maxAlert,
        color: "#FF5D6A",
        lineType: E_LineType.solid
      });
    }

    // 2. Tramo Advertencia Superior (entre maxWarn y maxAlert)
    if (maxWarn !== undefined) {
      if (maxAlert !== undefined) {
        pieces.push({
          gt: maxAlert - step,
          lt: maxAlert,
          color: "#EAAB25",
          lineType: E_LineType.solid
        });
        pieces.push({
          gt: maxWarn,
          lte: maxAlert - step,
          color: "#EAAB25",
          lineType: E_LineType.solid
        });
      } else {
        pieces.push({
          gt: maxWarn,
          color: "#EAAB25",
          lineType: E_LineType.solid
        });
      }
    }

    // 3. Tramo Normal (Rango Central)
    const hasUpperThreshold = maxWarn !== undefined || maxAlert !== undefined;
    const hasLowerThreshold = minWarn !== undefined || minAlert !== undefined;

    const upperLimit = maxWarn ?? maxAlert;
    const lowerLimit = minWarn ?? minAlert;

    if (hasUpperThreshold || hasLowerThreshold) {
      const normalPiece: any = {
        color: "#ffffff",
        lineType: E_LineType.solid
      };

      if (upperLimit !== undefined) {
        // Si solo existe maxAlert, generamos el tramo intermedio superior
        if (maxWarn === undefined && maxAlert !== undefined) {
          pieces.push({
            gt: maxAlert - step,
            lt: maxAlert,
            color: "#ffffff",
            lineType: E_LineType.solid
          });
          normalPiece.lte = maxAlert - step;
        } else {
          normalPiece.lte = upperLimit;
        }
      }

      if (lowerLimit !== undefined) {
        // Si solo existe minAlert, ajustamos la cota inferior del tramo normal
        if (minWarn === undefined && minAlert !== undefined) {
          normalPiece.gt = lowerLimit + step;
        } else {
          normalPiece.gt = lowerLimit;
        }
      }

      pieces.push(normalPiece);

      // Intermedio inferior si solo existe minAlert
      if (minWarn === undefined && minAlert !== undefined) {
        pieces.push({
          gt: minAlert,
          lt: minAlert + step,
          color: "#ffffff",
          lineType: E_LineType.solid
        });
      }
    }

    // 4. Tramo Advertencia Inferior (entre minAlert y minWarn)
    if (minWarn !== undefined) {
      if (minAlert !== undefined) {
        pieces.push({
          gt: minAlert + step,
          lte: minWarn,
          color: "#EAAB25",
          lineType: E_LineType.solid
        });
        pieces.push({
          gt: minAlert,
          lt: minAlert + step,
          color: "#EAAB25",
          lineType: E_LineType.solid
        });
      } else {
        pieces.push({
          lte: minWarn,
          color: "#EAAB25",
          lineType: E_LineType.solid
        });
      }
    }

    // 5. Tramo Crítico Inferior (<= minAlert)
    if (minAlert !== undefined) {
      pieces.push({
        lte: minAlert,
        color: "#FF5D6A",
        lineType: E_LineType.solid
      });
    }

    if (pieces.length === 0) return undefined;

    return {
      type: 'piecewise',
      dimension: 1,
      serieKey: name,
      show: false,
      pieces
    };
  }
}

export class C_ChartBaseConfig extends ChangesDetection {
  top: string = "7%";
  right: string = "7%";
  bottom: string = "7%";
  left: string = "7%";
  name: string = "";
  title: string = "";
  group: string = "";
  showLegend: boolean = true;
  numValues!: number;
  borderWidth?: number;
  showGrid?: boolean;
  borderColor?: string;
  baselineOffset: number = 0;

  constructor(baseConf?: I_ChartBaseConfigResponse) {
    super();
    if (baseConf) this.initializeFromJson(baseConf);
  }

  initializeFromJson(baseConf: I_ChartBaseConfigResponse) {
    this.top = baseConf.top;
    this.right = baseConf.right;
    this.bottom = baseConf.bottom;
    this.left = baseConf.left;
    this.name = baseConf.name;
    this.title = baseConf.title;
    this.group = baseConf.group;
    this.showLegend = baseConf.showLegend;
    this.numValues = baseConf.numValues;
    this.borderWidth = baseConf.borderWidth;
    this.showGrid = baseConf.showGrid;
    this.borderColor = baseConf.borderColor;
    if (baseConf.hasOwnProperty('baselineOffset')) this.baselineOffset = baseConf.baselineOffset!;
    this.forceUpdateDetection();
  }
}

export class ChartAxesConfigList {
  private list: Map<string, C_ChartAxisConfig> = new Map();

  constructor() { }

  addAxis(axis: I_ChartAxisConfigResponse) {
    this.list.set(axis.keyName, new C_ChartAxisConfig(axis));
  }

  getAllAxes(): C_ChartAxisConfig[] {
    return Array.from(this.list.values());
  }

  getAllXAxes(): C_ChartAxisConfig[] {
    return Array.from(this.list.values()).filter(axis => axis.position == E_ChartAxisPosition.Bottom || axis.position == E_ChartAxisPosition.Top);
  }

  getXAxis(key: string): undefined | C_ChartAxisConfig {
    return Array.from(this.list.values()).filter(axis => (axis.position == E_ChartAxisPosition.Bottom || axis.position == E_ChartAxisPosition.Top) && axis.keyName == key)[0];
  }

  getYAxis(key: string): undefined | C_ChartAxisConfig {
    return Array.from(this.list.values()).filter(axis => (axis.position == E_ChartAxisPosition.Left || axis.position == E_ChartAxisPosition.Right) && axis.keyName == key)[0];
  }

  getAllYAxes(): C_ChartAxisConfig[] {
    return Array.from(this.list.values()).filter(axis => axis.position == E_ChartAxisPosition.Left || axis.position == E_ChartAxisPosition.Right);
  }
}

export class C_ChartSerieConfig {
  key: string;
  lineWidth: number = 1;
  color?: string;
  candleKeyList: C_ChartCandleConfig[] = [];
  borderColor?: string;
  bgColor?: string;
  type: E_SmartDisplayGraphType;
  sourceType: E_GraphSerieSourceType;
  showSymbol: boolean = false;
  iconColor?: string;
  showColorOnLegend: boolean = false;
  showOnLegend: boolean = true;
  values: E_SmartDisplayGraphResult = E_SmartDisplayGraphResult.Last;
  axesNames: string[] = []; // Position 0 - X | Position 1 - Y
  aboveBaselineColor: string = '#79DA0F';
  belowBaselineColor: string = '#ED4965';
  marker: E_ChartMarkerIcon = E_ChartMarkerIcon.Kangaroo;
  balanceIntervalMs?: number;
  visualMap: C_VisualMap[] = [];

  constructor(serie: I_ChartSerieConfigResponse) {
    this.key = serie.key;
    if (serie.hasOwnProperty('linewidth')) this.lineWidth = serie.linewidth;
    if (serie.candleKeyList != null) {
      this.candleKeyList = [];
      serie.candleKeyList?.forEach(c => this.candleKeyList.push(new C_ChartCandleConfig(c)));
    }
    if (serie.color) this.color = serie.color;
    this.type = serie.type;
    this.sourceType = serie.sourceType;
    if (serie.axesNames) this.axesNames = serie.axesNames;
    if (serie.values) this.values = serie.values;
    if (serie.hasOwnProperty('showSymbol')) this.showSymbol = serie.showSymbol;
    if (serie.hasOwnProperty('showColorOnLegend')) this.showColorOnLegend = serie.showColorOnLegend;
    if (serie.hasOwnProperty('showOnLegend')) this.showOnLegend = serie.showOnLegend;
    if (serie.aboveBaselineColor) this.aboveBaselineColor = serie.aboveBaselineColor;
    if (serie.belowBaselineColor) this.belowBaselineColor = serie.belowBaselineColor;
    if (serie.markerIcon) this.marker = serie.markerIcon;
    if (serie.balanceIntervalMs) this.balanceIntervalMs = serie.balanceIntervalMs;
    if (serie.visualMap) {
      this.visualMap = [];

      /* EXPLANATION OF THE WORKAROUND:

      The charting library we're using has a limitation: a single 'visualMap'
      configuration can only apply one type of line style (e.g., solid, dashed)
      to a series at a time. It cannot handle multiple line types simultaneously
      within the same VisualMap.

      To overcome this and display different line styles on the same series
      (e.g., a solid line for one value range and a dashed line for another),
      we use a workaround. We create multiple 'visualMaps'—one for each unique
      line type present in the backend data. Each of these VisualMaps will
      contain all the original pieces, but we'll strategically make the colors
      of the "unwanted" pieces transparent.

      This approach allows the charting library to render a separate set of rules
      for each line style, effectively layering them on top of each other. The
      result is a single chart series that appears to have different line styles
      for different data segments, even though it's technically rendered using
      multiple VisualMap configurations.

      It's important to note that this is a two-part workaround. After creating these
      multiple VisualMap configurations, a separate piece of front-end logic is
      required to render them correctly. The chart series affected by this VisualMap
      will need to be replicated as many times as there are new VisualMap configurations.
      Each of these replicated series will use the exact same data but a different
      VisualMap from the array. This allows the charting library to apply each distinct
      line style (solid, dashed, etc.) as a separate visual layer on the same data,
      ultimately creating the desired effect of a single series with multiple line styles.

      */
      // Step 1: Identify all unique line types from the original data.
      // This is the core of the workaround, as we need to process each line type separately.
      const lineTypes = Array.from(new Set(serie.visualMap.pieces
        .map(p => p.lineType)
        .filter(lt => lt !== undefined)));

      // Step 2: Check if multiple line types are present.
      // The workaround is only necessary if there's more than one distinct line style.
      if (lineTypes.length > 1) {
        // Step 3: Iterate through each unique line type.
        // We create a separate VisualMap instance for each line type to "simulate"
        // support for multiple styles within a single series.
        lineTypes.forEach(lt => {
          // Step 3a: Map all original pieces to a new set of pieces for the current VisualMap.
          const pieces = serie.visualMap!.pieces.map(p => {
            // Step 3b: Clone the piece to avoid mutating the original data.
            const newPiece = { ...p };

            // Step 3c: Apply the "transparency trick".
            // If a piece's line type does not match the current line type we're processing,
            // we set its color to transparent. This effectively "hides" the piece from
            // the current VisualMap instance while ensuring all pieces are still
            // defined, which is required by the charting library.
            if (newPiece.lineType !== lt) {
              newPiece.color = 'transparent';
            }
            return new C_VisualMapPiece(newPiece);
          });

          // Step 3d: Push the newly created VisualMap to the final array.
          // Each of these VisualMaps will be rendered on top of each other,
          // creating the illusion of a single series with multiple line styles.
          const vm = new C_VisualMap({
            // Step 3e: Explicitly assign properties to satisfy the type-checker.
            // This is a necessary fix for a TypeScript issue where the spread operator
            // on an optional property (`serie.visualMap?`) can result in an
            // incorrect type inference (`boolean | undefined`).
            show: serie.visualMap!.show,
            dimension: serie.visualMap!.dimension,
            serieKey: serie.visualMap!.serieKey,
            pieces: [],
          });
          vm.pieces = pieces;
          this.visualMap.push(vm);
        });
      } else {
        // Step 4: Handle the default case.
        // If there's only one line type, no special logic is needed.
        // We simply use the original VisualMap as is.
        this.visualMap.push(new C_VisualMap(serie.visualMap));
      }
    }
  }
}
export class C_ChartCandleConfig {
  key: string = '';
  candleValueType: E_CandleValueType = E_CandleValueType.UNSET


  constructor(json: I_ChartCandleConfigResponse) {
    if (!json) return

    this.candleValueType = json.candleValueType;
    this.key = json.key;
  }
}
export class C_VisualMap {

  show: boolean = false;
  dimension: number = 0; //AXIS POSITION IN AXESNAMES
  serieKey: string = "";
  pieces: C_VisualMapPiece[] = [];
  seriesIndex?: number;

  constructor(vm?: I_VisualMapResponse) {
    if (!vm) return;
    if (vm.hasOwnProperty('show')) this.show = vm.show;
    if (vm.dimension != null && vm.dimension != undefined) this.dimension = vm.dimension;
    if (vm.serieKey) this.serieKey = vm.serieKey;
    if (vm.pieces) this.pieces = vm.pieces.map(p => new C_VisualMapPiece(p));
  }
}

export class C_VisualMapPiece {

  // COMPARISON OPERATORS
  gt?: number | Date;
  gte?: number | Date;
  lt?: number | Date;
  lte?: number | Date;
  eq?: number | string | boolean | Date;
  neq?: number | string | boolean | Date;

  // STYLES CONFIGURATION
  color: string = '#000000';
  symbol?: string;
  symbolSize?: number;
  opacity?: number;
  lineType: E_LineType = E_LineType.solid;

  constructor(vmPiece?: I_VisualMapPieceResponse) {
    if (!vmPiece) return;
    // 1. Estilos (limpio)
    this.color = vmPiece.color;
    this.symbol = vmPiece.symbol;
    this.symbolSize = vmPiece.symbolSize ?? undefined;
    this.opacity = vmPiece.opacity ?? undefined;
    this.lineType = (vmPiece.lineType?.toLowerCase() as E_LineType) || E_LineType.solid;

    // 2. Helper ultra-seguro
    const toNative = (val: any) => {
      if (typeof val !== 'string') return val;

      // Solo intentamos si tiene pinta de fecha (contiene guion o T de ISO)
      if (val.includes('-') || val.includes('T')) {
        const d = new Date(val);
        // d.getTime() devuelve NaN si la fecha es inválida
        return isNaN(d.getTime()) ? val : d;
      }
      return val;
    };

    // 3. Asignación directa
    if ('gt' in vmPiece) this.gt = toNative(vmPiece.gt);
    if ('gte' in vmPiece) this.gte = toNative(vmPiece.gte);
    if ('lt' in vmPiece) this.lt = toNative(vmPiece.lt);
    if ('lte' in vmPiece) this.lte = toNative(vmPiece.lte);
    if ('eq' in vmPiece) this.eq = toNative(vmPiece.eq);
    if ('neq' in vmPiece) this.neq = toNative(vmPiece.neq);
  }
}

export class C_ChartAxisConfig {
  type: E_ChartAxisType = E_ChartAxisType.Value;
  keyName: string;
  axisLine?: C_ChartAxisLine;
  position: E_ChartAxisPosition;
  min?: number | string;
  max?: number | string;
  axisTick?: C_ChartAxisTick;
  axisLabel: C_ChartAxisLabel = new C_ChartAxisLabel();
  offset: number = 0;
  labelFormat: E_SmartDisplayGraphLabel = E_SmartDisplayGraphLabel.Default;
  show: boolean = true;
  regularity: E_SmartDisplayGraphResult = E_SmartDisplayGraphResult.Shift;
  customLabels: string[] = [];

  constructor(axisConf: I_ChartAxisConfigResponse) {
    this.type = this.evaluateChartAxisType(axisConf.type);
    this.keyName = axisConf.keyName;
    if (axisConf.axisLine) this.axisLine = new C_ChartAxisLine(axisConf.axisLine);
    this.position = this.evaluateChartAxisPosition(axisConf.position);
    if (axisConf.min != null && axisConf.min != undefined) {
      if (typeof axisConf.min == "string") {
        this.min = (axisConf as I_ChartAxisConfigResponse).min;
      } else {
        this.min = Number(axisConf.min);
      }
    }
    if (axisConf.max != null && axisConf.max != undefined) {
      if (typeof axisConf.max == "string") {
        this.max = (axisConf as I_ChartAxisConfigResponse).max;
      } else {
        this.max = Number(axisConf.max);
      }
    }

    if (axisConf.axisTick) this.axisTick = new C_ChartAxisTick(axisConf.axisTick);
    if (axisConf.axisLabel) this.axisLabel = new C_ChartAxisLabel(axisConf.axisLabel);
    if (axisConf.hasOwnProperty('offset')) this.offset = axisConf.offset;
    if (axisConf.labelFormat) this.labelFormat = axisConf.labelFormat;
    if (axisConf.show != null && axisConf.show != undefined) this.show = axisConf.show;
    if (axisConf.regularity) this.regularity = axisConf.regularity;
    if (axisConf.customLabels) this.customLabels = axisConf.customLabels;
  }

  evaluateChartAxisType(type: E_ChartAxisTypeResponse): E_ChartAxisType {
    switch (type) {
      case E_ChartAxisTypeResponse.Value:
        return E_ChartAxisType.Value;
      default:
      case E_ChartAxisTypeResponse.Time:
        return E_ChartAxisType.Time;
    }
  }

  evaluateChartAxisPosition(pos: E_ChartAxisPositionResponse): E_ChartAxisPosition {
    switch (pos) {
      case E_ChartAxisPositionResponse.Bottom:
        return E_ChartAxisPosition.Bottom;
      case E_ChartAxisPositionResponse.Right:
        return E_ChartAxisPosition.Right;
      case E_ChartAxisPositionResponse.Top:
        return E_ChartAxisPosition.Top;
      default:
      case E_ChartAxisPositionResponse.Left:
        return E_ChartAxisPosition.Left;
    }
  }

}

export class C_ChartAxisLine {
  show: boolean = true;
  lineStyle?: C_AxisLineStyle;
  constructor(axisLine: I_ChartAxisLineResponse) {
    if (axisLine.hasOwnProperty('show')) this.show = axisLine.show;
    if (axisLine.lineStyle) this.lineStyle = new C_AxisLineStyle(axisLine.lineStyle);
  }
}

export class C_AxisLineStyle {

  color?: string;

  constructor(axisLineStyle: I_AxisLineStyleResponse) {
    if (axisLineStyle.color) this.color = axisLineStyle.color;
  }
}

export class C_ChartAxisTick {
  show: boolean = false;
  length?: number;
  interval?: number;

  constructor(axisTick: I_ChartAxisTickResponse) {
    if (axisTick.hasOwnProperty('show')) this.show = axisTick.show;
    if (!isNaN(axisTick.length)) this.length = axisTick.length;
    if (axisTick.interval != null && axisTick.interval != undefined) this.interval = axisTick.interval;
  }
}
export class C_ChartAxisLabel {

  show: boolean = true;
  color: string = '#ADB4B9';
  fontSize: number = 12;
  margin?: number;
  silent: boolean = false;

  constructor(axisLbl?: I_ChartAxisLabelResponse) {
    if (!axisLbl) return;
    if (axisLbl.show != undefined && axisLbl.show != null) this.show = !!axisLbl.show;
    if (axisLbl.color) this.color = axisLbl.color;
    this.fontSize = axisLbl.fontSize;
    if (!isNaN(axisLbl.margin)) this.margin = axisLbl.margin;
    if (axisLbl.silent != undefined && axisLbl.silent != null) this.silent = !!axisLbl.silent;
  }
}
